/**
 * Datumseingabe im deutschen Format „TT.MM.JJJJ".
 *
 * Natives <input type="date"> zeigt das Datum im Format der Browser-/OS-
 * Sprache (z. B. „10/26/2026") — erzwingen lässt sich das nicht. Deshalb ein
 * Textfeld „26.10.2026" plus Kalender-Button, der den nativen Picker eines
 * unsichtbaren <input type="date"> öffnet (gleiches Muster wie TimeInput24).
 *
 * Wert nach außen bleibt „YYYY-MM-DD" ("" = leer) — Aufrufer und API
 * ändern sich nicht. Freie Eingabe: „26.10.2026", „26.10.26", „261026",
 * „1.2.2026"; vollständige Eingaben werden sofort übernommen, der Rest beim
 * Verlassen des Feldes. Ungültiges wird verworfen (alter Wert bleibt).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, parseGermanDate } from '../lib/dateFormat';

export interface DateInputProps {
  /** „YYYY-MM-DD" (längere ISO-Strings werden auf das Datum gekürzt) oder "". */
  value: string | null | undefined;
  /** Neuer Wert „YYYY-MM-DD" oder "" (geleert). */
  onChange: (value: string) => void;
  ariaLabel?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  min?: string;
  max?: string;
  autoFocus?: boolean;
  /** Rahmen-Style (Border, Padding, Hintergrund …) — wie beim bisherigen <input>. */
  style?: React.CSSProperties;
  onBlur?: () => void;
  'data-testid'?: string;
}

const COMPLETE = /^(\d{1,2}\.\d{1,2}\.\d{4}|\d{4}-\d{2}-\d{2})$/;

export function DateInput({
  value, onChange, ariaLabel, id, name, disabled, readOnly, required, min, max,
  autoFocus, style, onBlur, 'data-testid': testId,
}: DateInputProps) {
  const { t: tr } = useTranslation();
  const stored = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
  const [draft, setDraft] = useState<string | null>(null);
  const nativeRef = useRef<HTMLInputElement | null>(null);
  // Neuer Stand von außen verwirft einen alten Entwurf.
  useEffect(() => { setDraft(null); }, [stored]);

  const outOfRange = (iso: string) => (min && iso < min) || (max && iso > max);

  const commit = () => {
    if (draft === null) return;
    const iso = parseGermanDate(draft);
    setDraft(null);
    if (iso === null || (iso && outOfRange(iso))) return;
    if (iso !== stored) onChange(iso);
  };

  const { width, ...boxStyle } = style ?? {};
  const inner: React.CSSProperties = {
    border: 'none', outline: 'none', background: 'transparent', color: 'inherit',
    font: 'inherit', padding: 0, minWidth: 0, flex: 1,
    fontVariantNumeric: 'tabular-nums',
  };

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 6, boxSizing: 'border-box',
        position: 'relative', width: width ?? '100%', ...boxStyle,
        opacity: disabled ? 0.6 : boxStyle.opacity,
      }}
    >
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        id={id}
        name={name}
        aria-label={ariaLabel}
        data-testid={testId}
        placeholder={tr('common.date_input.placeholder')}
        maxLength={10}
        value={draft ?? formatDate(stored, '')}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        autoFocus={autoFocus}
        onChange={e => {
          const v = e.target.value;
          setDraft(v);
          if (COMPLETE.test(v.trim())) {
            const iso = parseGermanDate(v);
            if (iso && !outOfRange(iso) && iso !== stored) onChange(iso);
          } else if (v.trim() === '' && stored) {
            onChange('');
          }
        }}
        onBlur={() => { commit(); onBlur?.(); }}
        onKeyDown={e => { if (e.key === 'Enter') commit(); }}
        style={inner}
      />
      {!readOnly && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={tr('common.date_input.open_calendar')}
          disabled={disabled}
          onClick={() => {
            const el = nativeRef.current;
            if (!el) return;
            try { el.showPicker?.(); } catch { el.focus(); }
          }}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: disabled ? 'default' : 'pointer',
            color: 'inherit', opacity: 0.55, display: 'flex', alignItems: 'center', flexShrink: 0,
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        </button>
      )}
      {/* Unsichtbarer nativer Picker — liefert die Kalenderauswahl. */}
      <input
        ref={nativeRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={stored}
        min={min}
        max={max}
        disabled={disabled}
        readOnly={readOnly}
        onChange={e => { setDraft(null); if (e.target.value !== stored) onChange(e.target.value); }}
        style={{
          position: 'absolute', right: 0, bottom: 0, width: 1, height: 1,
          opacity: 0, pointerEvents: 'none', border: 0, padding: 0,
        }}
      />
    </div>
  );
}
